import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { REDO_LEGACY_NUMBER_MAX, REDO_REASON_MAX } from '@/domain';
import { useArchive } from '@/application/usecases/useArchive';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import styles from './RedoFields.module.css';

/** The archive row behind the picked number, for the reassurance line only. */
interface PickedCase {
  jobOrderNo: string;
  dentistName: string | null;
  deliveredAt: string;
}

export interface RedoChoice {
  /**
   * The case this one remakes, or null while the case is not a remake. From
   * the archive it is the job order that was picked, and empty until one is;
   * from before this system it is the old number as it is being typed.
   */
  redoOf: string | null;
  /** The original is from before this system, so its number is typed, not found. */
  redoOfLegacy: boolean;
  redoReason: string;
}

/** What the form or the server says against the two fields of a remake. */
export interface RedoErrors {
  redoOf: string | null;
  redoReason: string | null;
}

const NO_ERRORS: RedoErrors = { redoOf: null, redoReason: null };

/**
 * Booking a returned case in as a remake, at intake. Section 7 of the request of
 * 16 September 2026.
 *
 * It used to be a button on the archive record which copied the original's
 * teeth, items, shade and specification. The client moved it here, and copies
 * nothing, and both are right: the case physically arrives back at the lab in a
 * box, somebody at reception opens it and books it in, and that is the same
 * motion as any other case. A remake is rarely identical, because the dentist
 * has usually sent a new impression, a different shade, or a note about what was
 * wrong, and a prefilled form invites somebody to accept last time's values
 * without reading them, which is exactly how the wrong shade gets made twice.
 *
 * The search needs no endpoint of its own: `GET /archive?q=` already searches
 * the archive by job order, doctor, patient and product, and a dispatched case
 * is what the archive holds.
 *
 * Since the request of 6 October 2026 (second), item 1, the original may also
 * be a case from before this system, which no archive search can find. The
 * person says which it is: in the archive, as before and still the choice the
 * block opens on, or from before this system, where the old job order number
 * is typed and nothing is searched. The reason is asked either way. Changing
 * the choice lets go of what the other one held, the case that was picked or
 * the number that was typed, and keeps the reason, which is about the box on
 * the desk and not about where its paperwork is.
 */
export function RedoFields({
  value,
  onChange,
  errors = NO_ERRORS,
}: {
  value: RedoChoice;
  onChange: (next: RedoChoice) => void;
  /** Shown against the field each is about. The form's own summary says it too. */
  errors?: RedoErrors;
}) {
  const [query, setQuery] = useState('');
  /*
   * The row that was picked, kept beside the number so the line under the
   * checkbox can name the doctor and the delivery date. Held here rather than
   * in the form value because it is a reassurance on screen, not part of the
   * case: the only thing that is sent is the job order number.
   */
  const [picked, setPicked] = useState<PickedCase | null>(null);
  const isRedo = value.redoOf !== null;
  const legacy = isRedo && value.redoOfLegacy;
  const ids = { where: useId(), numberProblem: useId(), reasonProblem: useId() };

  // Only once somebody has typed something worth searching, and only while
  // nothing is picked: the archive is thousands of cases.
  const searching = isRedo && !legacy && value.redoOf === '' && query.trim().length >= 2;
  const results = useArchive(
    useMemo(() => ({ q: query.trim(), pageSize: 6 }), [query]),
    { enabled: searching },
  );

  /*
   * A refusal is said beside the Create button, a screen away on a phone, and
   * against its field here. The block comes into view with it, so the person
   * is shown where to look rather than left to scroll back and find it.
   */
  const block = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errors.redoOf === null && errors.redoReason === null) return;
    block.current?.scrollIntoView?.({ block: 'nearest' });
  }, [errors.redoOf, errors.redoReason]);

  function toggle(on: boolean) {
    onChange(
      on
        ? { redoOf: '', redoOfLegacy: false, redoReason: '' }
        : { redoOf: null, redoOfLegacy: false, redoReason: '' },
    );
    setQuery('');
    setPicked(null);
  }

  /** In the archive, or from before this system. The other choice's value goes. */
  function chooseLegacy(next: boolean) {
    if (next === value.redoOfLegacy) return;
    onChange({ redoOf: '', redoOfLegacy: next, redoReason: value.redoReason });
    setQuery('');
    setPicked(null);
  }

  const archivePicked = isRedo && !legacy && value.redoOf !== '';

  return (
    <div className={styles.redo} ref={block}>
      <label className={styles.check}>
        <input
          type="checkbox"
          checked={isRedo}
          onChange={(event) => toggle(event.target.checked)}
        />
        <span>
          {strings.redo.isRedo}
          <small>{strings.redo.isRedoHint}</small>
        </span>
      </label>

      {isRedo && (
        <div className={styles.where} role="radiogroup" aria-label={strings.redo.originalWhere}>
          <label className={styles.choice}>
            <input
              type="radio"
              name={ids.where}
              checked={!legacy}
              onChange={() => chooseLegacy(false)}
            />
            <span>{strings.redo.originalInArchive}</span>
          </label>
          <label className={styles.choice}>
            <input
              type="radio"
              name={ids.where}
              checked={legacy}
              onChange={() => chooseLegacy(true)}
            />
            <span>{strings.redo.originalBeforeSystem}</span>
          </label>
        </div>
      )}

      {isRedo && !legacy && value.redoOf === '' && (
        <div className={styles.search}>
          <label className={styles.l} htmlFor="redo-search">
            {strings.redo.findCase}
          </label>
          <input
            id="redo-search"
            className={styles.input}
            value={query}
            placeholder={strings.redo.findPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
          />

          {searching && results.isPending && <p className={styles.hint}>{strings.redo.searching}</p>}
          {searching && !results.isPending && (results.data?.items.length ?? 0) === 0 && (
            <p className={styles.hint}>{strings.redo.noMatches}</p>
          )}

          <ul className={styles.results}>
            {(results.data?.items ?? []).map((row) => (
              <li key={row.jobOrderNo}>
                <button
                  type="button"
                  className={styles.result}
                  onClick={() => {
                    setPicked({
                      jobOrderNo: row.jobOrderNo,
                      dentistName: row.dentistName ?? null,
                      deliveredAt: row.deliveredAt,
                    });
                    onChange({ ...value, redoOf: row.jobOrderNo });
                  }}
                >
                  <b>{row.jobOrderNo}</b>
                  <span>
                    {row.dentistName ?? strings.orders.hidden} ·{' '}
                    {row.patientName ?? strings.orders.hidden}
                  </span>
                  <span className={styles.when}>
                    {strings.redo.delivered} {formatDay(row.deliveredAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {archivePicked && (
        <>
          {/*
            So the person can see they picked the right one. The doctor's name
            appears only if the caller was given one: a technician receives no
            identity, and a line here would be the one place it leaked.
          */}
          <p className={styles.picked}>
            {strings.redo.picked} <b>{value.redoOf}</b>
            {picked !== null && (
              <span className={styles.pickedOf}>
                {picked.dentistName === null ? '' : `${picked.dentistName} · `}
                {strings.redo.delivered} {formatDay(picked.deliveredAt)}
              </span>
            )}
            <Button variant="text" onClick={() => onChange({ ...value, redoOf: '' })}>
              {strings.redo.change}
            </Button>
          </p>
          {errors.redoOf !== null && <p className={styles.problem}>{errors.redoOf}</p>}
        </>
      )}

      {legacy && (
        <div className={styles.search}>
          {/*
            The old number, as it is written on the old job order: any letters
            and figures, so nothing here expects the pattern this system gives
            its own. Checked when the case is created, and by the server, which
            alone knows whether the number is a case of this system after all.
          */}
          <label className={styles.l} htmlFor="redo-legacy-number">
            {strings.redo.legacyNumber}
          </label>
          <input
            id="redo-legacy-number"
            className={styles.input}
            value={value.redoOf ?? ''}
            maxLength={REDO_LEGACY_NUMBER_MAX}
            placeholder={strings.redo.legacyNumberPlaceholder}
            autoComplete="off"
            aria-invalid={errors.redoOf !== null || undefined}
            aria-describedby={errors.redoOf !== null ? ids.numberProblem : undefined}
            onChange={(event) => onChange({ ...value, redoOf: event.target.value })}
          />
          {errors.redoOf !== null && (
            <p id={ids.numberProblem} className={styles.problem}>
              {errors.redoOf}
            </p>
          )}
        </div>
      )}

      {(archivePicked || legacy) && (
        <div className={styles.search}>
          <label className={styles.l} htmlFor="redo-reason">
            {strings.redo.reasonLabel}
          </label>
          <textarea
            id="redo-reason"
            className={styles.reason}
            value={value.redoReason}
            maxLength={REDO_REASON_MAX}
            placeholder={strings.redo.reasonPlaceholder}
            aria-invalid={errors.redoReason !== null || undefined}
            aria-describedby={errors.redoReason !== null ? ids.reasonProblem : undefined}
            onChange={(event) => onChange({ ...value, redoReason: event.target.value })}
          />
          {errors.redoReason !== null && (
            <p id={ids.reasonProblem} className={styles.problem}>
              {errors.redoReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
